import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./App.css";
import { AuthenticateDocument } from "./utils/AuthenticateDocument";
import MainLayout from "./components/MainLayout";
import External from "./components/ExternalUser";
import { slideAction } from "./store/slideSlice";
import { useDispatch } from "react-redux";
import { hasCachedDocument } from "./utils/documentCache";
import { getLocalPresentation, isLocalPresentationRoute } from "./utils/localPresentations";

function App({ isExternal = false, isTemplate = false }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  // Starts true: every branch below renders <AuthenticateDocument> only while
  // this is set, so a false initial value means auth never runs at all.
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  // Local (offline-created) presentations live only in localStorage and have no
  // server counterpart, so they skip auth and every fetch. Flagged by ?local=true.
  const isLocalPresentation = useMemo(() => isLocalPresentationRoute(), []);

  // Non-mutating cache probe — when a renderable entry exists we skip the auth
  // gating loader and paint MainLayout immediately (caching = normal docs only).
  const isDocumentCached = useMemo(
    () => !isTemplate && hasCachedDocument(id),
    [isTemplate, id]
  );

  useEffect(() => {
    dispatch(slideAction.setIsTemplateDocument(isTemplate));
  }, []);

  // Seed document info for local presentations so the header renders straight
  // away without an auth call. Owner-level rights, no members.
  useEffect(() => {
    if (isLocalPresentation) {
      const record = getLocalPresentation(id);
      if (record) {
        dispatch(slideAction.setDocumentInformation({
          id: record.id,
          name: record.name,
          creator: record.creator,
          createBy: record.creator?.id,
          members: [],
          privacyId: 1,
          rightType: 2,
          isLocal: true,
        }));
        dispatch(slideAction.setIsAuthenticated(true));
      }
    }
  }, [isLocalPresentation, id, dispatch]);

  if (isExternal) {
    return <External />;
  }

  // Local presentations: no auth gate, no fetch — render the editor straight away.
  if (isLocalPresentation) {
    return <MainLayout />;
  }

  // First visit (cache miss): gate behind the auth loader, unchanged.
  if (isAuthenticating && !isDocumentCached) {
    return <AuthenticateDocument setIsLoading={setIsAuthenticating} />;
  }

  // Cache hit: render MainLayout instantly and re-validate auth in the
  // background (no loader). Once auth resolves, only MainLayout remains.
  return (
    <>
      {isAuthenticating && isDocumentCached && (
        <AuthenticateDocument setIsLoading={setIsAuthenticating} background />
      )}
      <div className={isDocumentCached ? undefined : "app-content-fade-in"}>
        <MainLayout />
      </div>
    </>
  );
}

export default App;
